// ============================================================================
// SYSTELOS - StatCard Component
// ============================================================================
// Card de estatística/KPI padronizado para dashboards
// Versão: 1.1 | Atualizado: 04/01/2026
// CORREÇÃO: valor aceita number | string
// ============================================================================

import { ReactNode, MouseEvent } from 'react';
import { LucideIcon } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface StatCardProps {
  /** Label/título do card */
  label: string;
  /** Valor a exibir (número ou string formatada) */
  valor: number | string; // ✅ CORRIGIDO: era só number
  /** Ícone Lucide */
  icon: LucideIcon;
  /** Cor do ícone (nome da cor Tailwind) */
  cor: 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'teal' | 'gray' | 'pink';
  /** Formatar como moeda (R$) - só aplica se valor for number */
  isMoeda?: boolean;
  /** Subtítulo opcional abaixo do valor */
  subtitulo?: string; // ✅ ADICIONADO
  /** Callback de clique (torna o card clicável) */
  onClick?: () => void;
  /** Card está selecionado/ativo */
  ativo?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

interface StatCardGridProps {
  children: ReactNode;
  /** Número de colunas em diferentes breakpoints */
  colunas?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  /** Número de colunas fixo (alternativa a colunas) */
  cols?: number; // ✅ ADICIONADO
  /** Classes CSS adicionais */
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const COR_CLASSES: Record<string, string> = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  purple: 'bg-purple-500',
  teal: 'bg-teal-500',
  gray: 'bg-gray-500',
  pink: 'bg-pink-500',
};

const COR_ATIVO_BORDER: Record<string, string> = {
  blue: 'border-blue-500 ring-1 ring-blue-500',
  green: 'border-green-500 ring-1 ring-green-500',
  amber: 'border-amber-500 ring-1 ring-amber-500',
  red: 'border-red-500 ring-1 ring-red-500',
  purple: 'border-purple-500 ring-1 ring-purple-500',
  teal: 'border-teal-500 ring-1 ring-teal-500',
  gray: 'border-gray-500 ring-1 ring-gray-500',
  pink: 'border-pink-500 ring-1 ring-pink-500',
};

const COR_HOVER_BORDER: Record<string, string> = {
  blue: 'hover:border-blue-300',
  green: 'hover:border-green-300',
  amber: 'hover:border-amber-300',
  red: 'hover:border-red-300',
  purple: 'hover:border-purple-300',
  teal: 'hover:border-teal-300',
  gray: 'hover:border-gray-400',
  pink: 'hover:border-pink-300',
};

// ============================================================================
// HELPERS
// ============================================================================

const formatarMoeda = (valor: number): string => {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatarNumero = (valor: number): string => {
  return valor.toLocaleString('pt-BR');
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function StatCard({
  label,
  valor,
  icon: Icon,
  cor,
  isMoeda = false,
  subtitulo, // ✅ ADICIONADO
  onClick,
  ativo = false,
  className = '',
}: StatCardProps) {
  const corIconeClass = COR_CLASSES[cor];
  const corBorderAtivo = COR_ATIVO_BORDER[cor];
  const corBorderHover = COR_HOVER_BORDER[cor];
  const isClicavel = !!onClick;

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    onClick?.();
  };

  // ✅ CORRIGIDO: Formatação condicional baseada no tipo
  const valorFormatado = typeof valor === 'string' 
    ? valor 
    : isMoeda 
      ? formatarMoeda(valor) 
      : formatarNumero(valor);

  return (
    <div 
      className={`
        bg-white 
        rounded-xl 
        p-4
        border
        ${ativo ? corBorderAtivo : 'border-gray-200'}
        ${isClicavel ? `cursor-pointer ${corBorderHover} transition-all hover:shadow-md` : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
      onClick={isClicavel ? handleClick : undefined}
      role={isClicavel ? 'button' : undefined}
      tabIndex={isClicavel ? 0 : undefined}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">
            {label}
          </p>
          <p 
            className="text-2xl font-bold"
            style={{ color: '#0F1B3D' }}
          >
            {valorFormatado}
          </p>
          {subtitulo && (
            <p className="text-xs text-gray-400 mt-1">{subtitulo}</p>
          )}
        </div>
        <div 
          className={`
            w-12 h-12 
            rounded-full 
            flex items-center justify-center 
            ${corIconeClass}
          `}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GRID COMPONENT
// ============================================================================

export function StatCardGrid({
  children,
  colunas = { mobile: 2, tablet: 3, desktop: 5 },
  cols, // ✅ ADICIONADO
  className = '',
}: StatCardGridProps) {
  // Se cols foi passado, usa ele para todas as breakpoints
  const { mobile = 2, tablet = 3, desktop = 5 } = cols 
    ? { mobile: cols, tablet: cols, desktop: cols }
    : colunas;
  
  // Mapear para classes Tailwind
  const colunasClasses: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  };

  const mobileClass = colunasClasses[mobile] || 'grid-cols-2';
  const tabletClass = `md:${colunasClasses[tablet] || 'grid-cols-3'}`;
  const desktopClass = `lg:${colunasClasses[desktop] || 'grid-cols-5'}`;

  return (
    <div 
      className={`
        grid 
        ${mobileClass} 
        ${tabletClass} 
        ${desktopClass} 
        gap-4 
        mb-6
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {children}
    </div>
  );
}

// ============================================================================
// COMPOUND EXPORTS
// ============================================================================

StatCard.Grid = StatCardGrid;

export default StatCard;

